import { useState } from 'react';

export const DRAFT_STORAGE_PREFIX = 'process-draft:';

const read = (key) => {
  if (!key) return {};
  try {
    const raw = window.localStorage.getItem(DRAFT_STORAGE_PREFIX + key);
    return raw ? JSON.parse(raw).v || {} : {};
  } catch {
    return {};
  }
};

const write = (key, value) => {
  if (!key) return;
  try {
    const storageKey = DRAFT_STORAGE_PREFIX + key;
    if (Object.keys(value).length) {
      window.localStorage.setItem(storageKey, JSON.stringify({ v: value, ts: Date.now() }));
    } else {
      window.localStorage.removeItem(storageKey);
    }
  } catch {
    // Storage full or blocked (private mode): the draft still lives in memory.
  }
};

/**
 * Form draft mirrored to localStorage under `storageKey`, so a failed save, a
 * crash or a reload does not throw away what was typed. Without a key it is a
 * plain in-memory draft. Switching key loads the draft stored for the new one.
 *
 * Stale entries are pruned at startup by the TTL sweep in `src/main.jsx`.
 */
export default function useStoredDraft(storageKey) {
  const [state, setState] = useState(() => ({ key: storageKey, draft: read(storageKey) }));

  // Key changed (another process, or the stage moved on): reload during render.
  let current = state;
  if (state.key !== storageKey) {
    current = { key: storageKey, draft: read(storageKey) };
    setState(current);
  }

  const setDraft = (update) =>
    setState((s) => {
      const draft = typeof update === 'function' ? update(s.draft) : update;
      write(s.key, draft);
      return { ...s, draft };
    });

  return [current.draft, setDraft];
}
